import React, {useEffect, useState} from "react";
import {Table, Modal} from "react-bootstrap";
import {DashboardHero, DashboardMetrics, DashboardLinks, DashboardPanel, DashboardEmpty, formatAmount, localDay} from './dashboardui';
import { useSuppliers } from "../SupplierContext";
import { usePurchases } from "../PurchaseContext";
import { useProducts } from "../ProductContext";
import {useUser} from '../UserContext';
import {canViewSupplierReports} from './inventory/supplierreports';

function Inventory() {
    const {user} = useUser();
    // this component would ideally use state and effects to load the summary data
    const { suppliers, fetchAllSuppliers } = useSuppliers();
    const [suppliersWithCreditBalance, setSuppliersWithCreditBalance] = useState([]);
    const [totalOutstandingBalance, setTotalOutstandingBalance] = useState(0);

    const { purchases, fetchAllPurchases } = usePurchases();
    const [todaysPurchases, setTodaysPurchases] = useState([]);
    const [totalPurchasesValue, setTotalPurchasesValue] = useState(0);

    // state for purchase details modal
    const [showPurchaseModal, setShowPurchaseModal] = useState(false);
    const [selectedPurchase, setSelectedPurchase] = useState(null);

    // state for product details
    const {fetchAllProducts, totalProductsCount, fetchAllProductsForStockValue, fetchAllProductsForRecent,
    } = useProducts();
    const [totalStockValue, setTotalStockValue] = useState(0);
    const [recentProducts, setRecentProducts] = useState([]);
    const [stockError, setStockError] = useState('');
    const [recentError, setRecentError] = useState('');


    useEffect(() => {
        fetchAllSuppliers();
        fetchAllPurchases();
        fetchAllProducts();
    }, [fetchAllSuppliers, fetchAllPurchases, fetchAllProducts]);

    useEffect(() => {
        if (suppliers) {
            // filter suppliers with credit balance
            const filteredSuppliers = suppliers.filter(supplier => parseFloat(supplier.balance) > 0);
            setSuppliersWithCreditBalance(filteredSuppliers);

            // calculate total outstanding balance
            const total = filteredSuppliers.reduce((acc, supplier) => acc + parseFloat(supplier.balance), 0);
            setTotalOutstandingBalance(total);
        }
    }, [suppliers]);

    // purchases logic
    useEffect(() => {
        if (purchases) {
            // filter purchases for today
            const today = localDay();
            const filteredPurchases = purchases.filter(purchase => {
                const purchaseDate = purchase.purchase_date || purchase.created_at;
                return purchaseDate && purchaseDate.startsWith(today);
            });
            setTodaysPurchases(filteredPurchases);

            // calculate total purchases value
            const total = filteredPurchases.reduce((acc, purchase) => acc + parseFloat(purchase.total_amount), 0);
            setTotalPurchasesValue(total);
        }
    }, [purchases]);

    // total stock value
    useEffect(() => {
        const calculateTotalStockValue = async () => {
            const allProducts = await fetchAllProductsForStockValue();

            // Calculate total stock value
            const totalValue = allProducts.reduce((acc, product) => {
                return acc + parseFloat(product.stock) * parseFloat(product.cost_price);
            }, 0);

            setTotalStockValue(totalValue);

            // Update total products count (if needed)
            // setTotalProducts(allProducts.length);
        };

        calculateTotalStockValue().catch(() => setStockError('Stock value unavailable'));
    }, [fetchAllProductsForStockValue]);


    // recently added products
    useEffect(() => {
        const fetchRecentProducts = async () => {
            const allProducts = await fetchAllProductsForRecent();

            const sortedProducts = [...allProducts].sort((a, b) => {
                return new Date(b.created_at) - new Date(a.created_at);
            });

            setRecentProducts(sortedProducts.slice(0, 5));
        };

        fetchRecentProducts().catch(() => setRecentError('Could not load recent products.'));
    }, [fetchAllProductsForRecent]);


    const handleShowPurchaseDetails = (purchase) => {
        setSelectedPurchase(purchase);
        setShowPurchaseModal(true);
    };

    const handleClosePurchaseModal = () => {
        setSelectedPurchase(null);
        setShowPurchaseModal(false);
    }

    return (
        <main className="wh-dashboard">
            <DashboardHero eyebrow="BEEQASI / INVENTORY" title="Stock in focus." description="Manage your catalogue, supplier relationships and daily stock activity." to="/inventory/purchases" action="Open purchases" />
            <DashboardMetrics items={[
                {label:'Products',value:totalProductsCount,hint:'Product catalogue',icon:'bi-boxes',to:'/inventory/products'},
                {label:'Stock value',value:stockError || `KES ${formatAmount(totalStockValue)}`,hint:'Company-wide stock × recorded cost',icon:'bi-box-seam',tone:'blue',to:'/inventory/store-inventory'},
                {label:'Purchases today',value:`KES ${formatAmount(totalPurchasesValue)}`,hint:'Today’s purchases in the recent purchase feed',icon:'bi-bag-check',to:'/inventory/purchases'},
                {label:'Supplier balances',value:`KES ${formatAmount(totalOutstandingBalance)}`,hint:`${suppliersWithCreditBalance.length} suppliers with positive balances`,icon:'bi-wallet2',tone:'amber',to:'/inventory/suppliers'},
            ]} />
            <div className="wh-section-heading"><h2>Inventory workspace</h2><span>From receiving stock to replenishing branches</span></div>
            <DashboardLinks items={[
                ['/inventory/products','bi-box','Products','Catalogue, pricing and reconciliation'],
                ['/inventory/product-report','bi-bar-chart','Product report','Movement and product performance'],
                ['/inventory/purchases','bi-bag-plus','Purchases','Record incoming supplier stock'],
                ['/inventory/suppliers','bi-truck','Suppliers','Contacts, balances and payments'],
                ...(canViewSupplierReports(user) ? [['/inventory/supplier-reports','bi-tags','Supplier reports','Compare recent prices, spending and supplier performance']] : []),
                ...(canViewSupplierReports(user) ? [['/inventory/operations-insights','bi-activity','Operations insights','Stock cover, idle stock and delivery exceptions']] : []),
                ['/inventory/store-inventory','bi-shop','Store inventory','Stock availability by store'],
                ['/inventory/stock-ledger','bi-journal-text','Stock ledger','Trace movements and adjustments'],
                ['/inventory/reorder-levels?tab=alerts','bi-bell','Reorder levels & alerts','Plan what to replenish next'],
                ['/inventory/replenishment-plans','bi-journal-check','Rough order plans','Supplier spending plans and branch transfer priorities'],
                ['/inventory/stock-transfer','bi-arrow-left-right','Stock transfers','Manage requests and branch supply'],
            ]} />
            <div className="wh-section-heading"><h2>Latest activity</h2><span>Purchases, supplier balances and catalogue additions</span></div>
            <div className="dash-split">
                <DashboardPanel title="Today’s recent purchases" subtitle="Open a supplier entry to inspect the purchase" to="/inventory/purchases">
                    {todaysPurchases.length ? <div className="wh-table-wrap"><table><thead><tr><th>Supplier</th><th>Invoice</th><th>Amount (KES)</th></tr></thead><tbody>{todaysPurchases.map(purchase => <tr key={purchase.id}><td><button type="button" className="dash-purchase-button" onClick={() => handleShowPurchaseDetails(purchase)}>{purchase.supplier_name}</button></td><td>{purchase.invoice_number || '—'}</td><td>{formatAmount(purchase.total_amount)}</td></tr>)}</tbody></table></div> : <DashboardEmpty>No purchases dated today in the recent feed.</DashboardEmpty>}
                </DashboardPanel>
                <DashboardPanel title="Supplier balances" subtitle="All outstanding balances · highest first" to="/inventory/suppliers">
                    {suppliersWithCreditBalance.length ? <div className="wh-table-wrap"><table><thead><tr><th>Supplier</th><th>Balance (KES)</th></tr></thead><tbody>{[...suppliersWithCreditBalance].sort((a,b) => Number(b.balance) - Number(a.balance)).map(supplier => <tr key={supplier.id}><td>{supplier.supplier_name}</td><td>{formatAmount(supplier.balance)}</td></tr>)}</tbody></table></div> : <DashboardEmpty>No positive supplier balances.</DashboardEmpty>}
                </DashboardPanel>
                <div className="dash-full-row">
                    <DashboardPanel title="Recently added products" subtitle="The latest five additions to your catalogue" to="/inventory/products">
                        {recentError ? <div className="dash-error">{recentError}</div> : recentProducts.length ? <ul className="dash-list">{recentProducts.map(product => <li key={product.id}><strong>{product.product_name}</strong><small>Added {new Date(product.created_at).toLocaleDateString('en-GB')}</small></li>)}</ul> : <DashboardEmpty>No recent product additions.</DashboardEmpty>}
                    </DashboardPanel>
                </div>
            </div>
            <p className="dash-footnote">Stock value uses the existing company-wide catalogue valuation. For stock held at a specific store, open Store Inventory.</p>

             {/* Purchase Details Modal */}
            <Modal show={showPurchaseModal} onHide={handleClosePurchaseModal} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Purchase Details</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedPurchase && (
                        <>
                            <h5>Supplier: {selectedPurchase.supplier_name}</h5>
                            <h5>Date: {new Date(selectedPurchase.purchase_date || selectedPurchase.created_at).toLocaleDateString('en-GB')}</h5>
                            <h5>Invoice Number: {selectedPurchase.invoice_number}</h5>
                            <h5>Total Amount: {parseFloat(selectedPurchase.total_amount).toFixed(2)}</h5>
                            {/* Display purchase items if available */}
                            <Table striped bordered hover responsive>
                                <thead>
                                    <tr>
                                        <th>Product</th>
                                        <th>Quantity</th>
                                        <th>Purchase Price</th>
                                        <th>Subtotal</th>
                                    </tr>
                                </thead>
                                <tbody>
                                      {(selectedPurchase.items || []).map((item, index) => (
                                         <tr key={index}>
                                            <td>{item.product_name} {item.unit_name? `${item.unit_name}` : ''}</td>
                                            <td>{item.quantity}</td>
                                            <td>{parseFloat(item.purchase_price).toFixed(2)}</td>
                                            <td>{(item.quantity * item.purchase_price).toFixed(2)}</td>
                                         </tr>
                                      ))}
                                </tbody>
                            </Table>
                        </>
                    )}
                </Modal.Body>
            </Modal>
        </main>
    );
}

export default Inventory;
